/**
 * Formen, in denen ein Video läuft (7.82).
 *
 * Sascha, 12. September, in der App als DRINGEND: „Ich hätte gerne eine
 * Gedankenblase in der ich ein Video abspielen kann. (Wenn eine Person was
 * denkt)“.
 *
 * Eine Ebene über dem Grundmaterial ist seit 7.55 ein echtes Videoelement.
 * Was fehlte, ist die Form davor: nicht das ganze Rechteck zeigen, sondern
 * einen Kreis, eine Ellipse, ein Rechteck mit runden Ecken -- oder eben eine
 * Gedankenblase mit ihren zwei Bläschen.
 *
 * **Warum das hier steht und nicht zweimal woanders:** die Vorschau zeichnet
 * im Browser, der Export rechnet in ffmpeg. Zwei Umsetzungen derselben Form
 * gehen garantiert auseinander -- und eine Vorschau, die etwas anderes zeigt
 * als der Export, ist schlimmer als keine. Also steht die Geometrie *einmal*
 * hier, und beide Seiten leiten sich daraus ab:
 *
 *   - `geqAlpha()` gibt den Ausdruck für ffmpegs `geq`,
 *   - `svgFormen()` gibt dieselben Formen für das `<clipPath>` im Browser,
 *   - und `drin()` sagt für einen Punkt, wie deckend er ist -- die Prüfungen
 *     vergleichen damit beide Wege gegen dieselbe Wahrheit.
 *
 * Gerechnet wird in Anteilen der Ebene (0..1 in beiden Richtungen), damit
 * dieselbe Form auf jeder Grösse sitzt.
 */
/** Die Formen, in der Reihenfolge, in der sie im Blatt stehen. */
export const MASKENFORMEN = ['keine', 'kreis', 'ellipse', 'ecken', 'blase', 'sprechblase'];
export const MASKEN_NAMEN = {
    keine: 'Ganzes Bild',
    kreis: 'Kreis',
    ellipse: 'Ellipse',
    ecken: 'Runde Ecken',
    blase: 'Gedankenblase',
    sprechblase: 'Sprechblase',
};
/**
 * Aus welchen Teilen eine Form besteht.
 *
 * Alles ausser „runde Ecken“ ist eine **Vereinigung von Ellipsen** -- die
 * Gedankenblase ist der grosse Kopf und zwei Bläschen darunter. Das ist
 * absichtlich schlicht: eine Vereinigung von Ellipsen lässt sich in ffmpeg
 * als Maximum weniger Ausdrücke schreiben und im Browser als ein paar
 * `<ellipse>`-Elemente, und beide Seiten kommen ohne Kunstgriffe aus.
 */
export function ellipsen(form) {
    switch (form) {
        case 'kreis':
            // Ein Kreis im Bild: der kleineren Seite folgend, damit er rund bleibt
            // und nicht zur Ellipse wird. Das macht `rundMachen` beim Zeichnen.
            return [{ cx: 0.5, cy: 0.5, rx: 0.5, ry: 0.5 }];
        case 'ellipse':
            return [{ cx: 0.5, cy: 0.5, rx: 0.5, ry: 0.5 }];
        case 'blase':
            return [
                // Der Kopf als **Wolke** (7.224; Sascha, 4. Oktober: „Mehr wie ein
                // Comic sollte sie aussehen“): ein Kern und rundherum Bögen, die
                // über seinen Rand hinausstehen. Auch das ist nur eine Vereinigung
                // von Ellipsen -- Vorschau, Bildmaler und Export bleiben bei ihrer
                // einen Rechnung. Aussen reicht die Wolke genau so weit wie der
                // glatte Kopf vorher, der Rahmen der Anfasser bleibt also derselbe.
                ...wolke({ cx: 0.5, cy: 0.42, rx: 0.5, ry: 0.42 }, WOLKE_BOEGEN, WOLKE_KERN),
                // Drei Bläschen nach unten links, kleiner werdend -- so denkt man in
                // Comics seit hundert Jahren.
                { cx: 0.31, cy: 0.875, rx: 0.07, ry: 0.062 },
                { cx: 0.2, cy: 0.94, rx: 0.048, ry: 0.043 },
                { cx: 0.11, cy: 0.978, rx: 0.03, ry: 0.022 },
            ];
        case 'sprechblase':
            // **Die Sprechblase** (7.224, Teil 4: „als Variante die Sprechblase
            // mit Spitze statt Bläschen“): ein glatter Körper, und die Spitze nach
            // unten links als Kette kleiner werdender Kreise, die einander
            // überlappen -- weiter nur Ellipsen, also dieselbe eine Rechnung in
            // Vorschau, Bildmaler und Export.
            return [{ cx: 0.5, cy: 0.42, rx: 0.5, ry: 0.42 }, ...kette([0.36, 0.79], [0.1, 0.985], 0.085, 0.01)];
        default:
            return [];
    }
}
/**
 * Eine Spitze aus Kreisen (7.224): von `von` nach `bis`, der Radius nimmt
 * gleichmässig von `r0` auf `r1` ab, und jeder nächste Kreis liegt so nah,
 * dass er den vorigen deutlich überlappt -- die Kante bleibt eine Spitze und
 * zerfällt nicht in Perlen.
 */
export function kette(von, bis, r0, r1) {
    const laenge = Math.hypot(bis[0] - von[0], bis[1] - von[1]);
    const ux = (bis[0] - von[0]) / laenge;
    const uy = (bis[1] - von[1]) / laenge;
    const teile = [];
    for (let s = 0; s <= laenge + 1e-9;) {
        const r = r0 + (r1 - r0) * (s / laenge);
        teile.push({ cx: von[0] + ux * s, cy: von[1] + uy * s, rx: r, ry: r });
        s += 1.1 * r;
    }
    return teile;
}
/** Wie viele Bögen die Wolke der Gedankenblase hat (7.224). */
export const WOLKE_BOEGEN = 10;
/** Wie weit der Kern der Wolke reicht, als Anteil des ganzen Kopfes. */
export const WOLKE_KERN = 0.74;
/**
 * Höchstens so viele Ellipsen hat eine Form -- der Bildmaler im Browser
 * reserviert dafür feste Plätze (`glmaler.ts`).
 */
export const MASKE_ELLIPSEN_MAX = 16;
/**
 * Eine Wolke in der Ellipse `e`: ein Kern mit dem Anteil `kern` der Radien,
 * und `boegen` gleich grosse Bögen auf seinem Rand, die genau bis an den Rand
 * von `e` reichen. Nebeneinander überlappen sie ein wenig; dazwischen bleibt
 * die Kerbe, an der man eine Wolke erkennt.
 */
export function wolke(e, boegen, kern) {
    const teile = [{ cx: e.cx, cy: e.cy, rx: e.rx * kern, ry: e.ry * kern }];
    for (let i = 0; i < boegen; i++) {
        const w = (2 * Math.PI * i) / boegen;
        teile.push({
            cx: e.cx + e.rx * kern * Math.cos(w),
            cy: e.cy + e.ry * kern * Math.sin(w),
            rx: e.rx * (1 - kern),
            ry: e.ry * (1 - kern),
        });
    }
    return teile;
}
/** Der Eckenradius für „runde Ecken“, in Anteilen der kleineren Seite. */
export const ECKEN_RADIUS = 0.14;
export const LAGE_MITTIG = { x: 0.5, y: 0.5, groesse: 1, drehung: 0 };
/** Steht die Form so, wie sie vor 7.82b immer stand? */
export function maskeMittig(l) {
    return l.x === 0.5 && l.y === 0.5 && l.groesse === 1 && l.drehung === 0;
}
/**
 * Ein Punkt der Ebene, zurückgerechnet ins Feld der Form.
 *
 * Gerechnet wird in Bildpunkten: erst mit `breite`/`hoehe` hinein, dann
 * drehen und teilen, dann wieder heraus. In Anteilen zu drehen wäre kürzer
 * und falsch -- auf einer 16:9-Ebene würde ein Kreis dabei kippen.
 */
export function insFeld(u, v, l, breite = 1, hoehe = 1) {
    const g = Math.max(1e-6, l.groesse);
    // Zurückgedreht, also mit dem umgekehrten Winkel.
    const t = (-l.drehung * Math.PI) / 180;
    const c = Math.cos(t);
    const s = Math.sin(t);
    const dx = (u - l.x) * breite;
    const dy = (v - l.y) * hoehe;
    return [(dx * c - dy * s) / g / (breite || 1) + 0.5, (dx * s + dy * c) / g / (hoehe || 1) + 0.5];
}
/** Ein Kreis soll rund bleiben: die kleinere Seite gibt den Radius vor. */
function rundMachen(form, e, breite, hoehe) {
    if (form !== 'kreis' || breite <= 0 || hoehe <= 0)
        return e;
    const r = Math.min(breite, hoehe) / 2;
    return { ...e, rx: r / breite, ry: r / hoehe };
}
/**
 * Wie deckend ein Punkt ist: 1 mitten drin, 0 draussen, dazwischen die weiche
 * Kante. `u`/`v` sind Anteile der Ebene.
 *
 * Das ist die Wahrheit, gegen die beide Umsetzungen geprüft werden.
 */
export function drin(form, u, v, weich = 0, breite = 1, hoehe = 1, lage = LAGE_MITTIG, 
/**
 * So weit nach innen gerückt (7.224): die Fläche *innerhalb* der Kontur.
 * Dieselben Anteile wie `weich` -- die Kontur ist, was zwischen `drin(…, 0)`
 * und `drin(…, staerke)` liegt.
 */
innen = 0) {
    if (form === 'keine')
        return 1;
    const w = Math.max(0, Math.min(0.5, weich));
    if (!maskeMittig(lage)) {
        const [uf, vf] = insFeld(u, v, lage, breite, hoehe);
        u = uf;
        v = vf;
    }
    if (lage.gespiegelt)
        u = 1 - u;
    if (form === 'ecken') {
        // Abstand zum Rechteck mit runden Ecken, in Anteilen der kleineren Seite
        // gerechnet, damit die Ecken auf einem breiten Bild nicht oval werden.
        const k = Math.min(breite, hoehe) || 1;
        const halbB = breite / 2;
        const halbH = hoehe / 2;
        const r = ECKEN_RADIUS * k;
        const qx = Math.abs(u - 0.5) * breite - (halbB - r);
        const qy = Math.abs(v - 0.5) * hoehe - (halbH - r);
        const abstand = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - r;
        return kante(-abstand / k - innen, w);
    }
    let beste = 0;
    for (const roh of ellipsen(form)) {
        const e = rundMachen(form, roh, breite, hoehe);
        const d = Math.hypot((u - e.cx) / e.rx, (v - e.cy) / e.ry);
        beste = Math.max(beste, kante((1 - d) * Math.min(e.rx, e.ry) - innen, w));
    }
    return beste;
}
/**
 * Die weiche Kante: `tiefe` ist der Abstand nach innen (positiv = drin),
 * gemessen in Anteilen der Ebene. Ohne weiche Kante ist es eine Stufe.
 */
function kante(tiefe, weich) {
    if (weich <= 0)
        return tiefe >= 0 ? 1 : 0;
    return Math.max(0, Math.min(1, tiefe / weich + 0.5));
}
// ---------------------------------------------------------------------------
// Die beiden Ableitungen
// ---------------------------------------------------------------------------
const z = (n) => String(Math.round(n * 1e6) / 1e6);
/**
 * Der Ausdruck für ffmpegs `geq`, der den Alphakanal setzt.
 *
 * `X`/`Y` sind Bildpunkte, `W`/`H` die Grösse der Ebene -- ffmpeg füllt sie
 * selbst ein. Herauskommt 0..255.
 *
 * Die weiche Kante ist dieselbe Geradenstückelung wie in `drin()`: keine
 * Kunst, aber beide Seiten rechnen sie gleich, und darauf kommt es an.
 */
export function geqAlpha(form, weich = 0, lage = LAGE_MITTIG, innen = 0) {
    if (form === 'keine')
        return null;
    const w = Math.max(0, Math.min(0.5, weich));
    // Nach innen gerückt (7.224, die Fläche innerhalb der Kontur): jede Tiefe
    // um `innen` weniger -- wie in `drin()`.
    const stufe = (roh) => {
        const tiefe = innen > 0 ? `(${roh}-${z(innen)})` : roh;
        return w <= 0 ? `gte(${tiefe},0)` : `clip(${tiefe}/${z(w)}+0.5,0,1)`;
    };
    // `W` und `H` bleiben stehen: die wahre Grösse einer Ebene steht beim
    // Übersetzen gar nicht fest -- sie behält das Seitenverhältnis ihrer
    // Quelle, und das kennt erst ffmpeg. Also rechnet der Ausdruck selbst
    // damit, statt eine Zahl einzubacken, die nur manchmal stimmt.
    const kurz = 'min(W,H)';
    const lang = 'max(W,H)';
    // Der Punkt, an dem gefragt wird -- zurückgerechnet ins Feld der Form,
    // genau wie in `insFeld()`. Steht die Form mittig, bleibt es bei `X`/`Y`:
    // der Ausdruck soll nicht bei jedem Clip länger werden, nur weil es die
    // Möglichkeit gibt.
    let px = 'X';
    let py = 'Y';
    if (!maskeMittig(lage)) {
        const g = Math.max(1e-6, lage.groesse);
        const t = (-lage.drehung * Math.PI) / 180;
        const c = Math.cos(t);
        const s = Math.sin(t);
        const dx = `(X-${z(lage.x)}*W)`;
        const dy = `(Y-${z(lage.y)}*H)`;
        px = `((${dx}*${z(c)}-${dy}*${z(s)})/${z(g)}+W/2)`;
        py = `((${dx}*${z(s)}+${dy}*${z(c)})/${z(g)}+H/2)`;
    }
    if (form === 'ecken') {
        const r = `(${z(ECKEN_RADIUS)}*${kurz})`;
        const qx = `(abs(${px}-W/2)-(W/2-${r}))`;
        const qy = `(abs(${py}-H/2)-(H/2-${r}))`;
        const abstand = `(hypot(max(${qx},0),max(${qy},0))-${r})`;
        return `255*${stufe(`(-${abstand}/${kurz})`)}`;
    }
    // Der Punkt in Anteilen, **einmal** ausgerechnet und abgelegt (`st`/`ld`):
    // bei verschobener oder gedrehter Form stünde die Umrechnung sonst in jedem
    // Teil noch einmal -- bei der Wolke vierzehnmal je Bildpunkt (7.224).
    const u = 'ld(0)';
    const v = 'ld(1)';
    // Gespiegelt (7.224) wird hier, im Feld -- wie in `drin()`.
    const vorspann = `st(0,${lage.gespiegelt ? `1-${px}/W` : `${px}/W`});st(1,${py}/H)`;
    const teil = (e) => {
        // Ein Kreis folgt der kleineren Seite, damit er rund bleibt; alles andere
        // füllt seinen Anteil aus.
        const rx = form === 'kreis' ? `(${kurz}/(2*W))` : z(e.rx);
        const ry = form === 'kreis' ? `(${kurz}/(2*H))` : z(e.ry);
        const kleinerer = form === 'kreis' ? `(${kurz}/(2*${lang}))` : z(Math.min(e.rx, e.ry));
        const d = `hypot((${u}-${z(e.cx)})/${rx},(${v}-${z(e.cy)})/${ry})`;
        return stufe(`((1-${d})*${kleinerer})`);
    };
    // Vereinigung = das Maximum. ffmpeg kann `max` nur mit zwei Werten, also
    // geschachtelt.
    const vereint = (teile) => teile.reduce((a, b) => `max(${a},${b})`);
    if (form === 'blase') {
        // **Die Wolke, ohne jeden Bildpunkt vierzehnmal zu fragen** (7.224).
        // `ffmpeg` rechnet `geq` für jeden Punkt jedes Bildes; mit allen Teilen
        // war der Export fünf- bis neunmal so langsam. Das Ergebnis steht aber
        // meist vorher fest: ausserhalb des Kopfes (samt weicher Kante) ist der
        // Kopf 0, im Kern ist er 1 -- nur auf dem Ring dazwischen zählen die
        // Bögen. `if` rechnet nur den Zweig, den es nimmt. Die Form bleibt genau
        // dieselbe Vereinigung wie in `drin()`.
        const alle = ellipsen('blase');
        const kern = alle[0];
        const boegen = alle.slice(1, 1 + WOLKE_BOEGEN);
        const blaeschen = alle.slice(1 + WOLKE_BOEGEN);
        const kopf = { cx: kern.cx, cy: kern.cy, rx: kern.rx / WOLKE_KERN, ry: kern.ry / WOLKE_KERN };
        // Weiter als bis hier reicht kein Bogen, auch nicht mit weicher Kante:
        // ein Bogen (Radius 1-k um k, im Feld des Kopfes) endet bei
        // 1 + w/(2·min(rx, ry)) -- der Kern davor.
        const grenze = 1 + w / (2 * Math.min(kopf.rx, kopf.ry)) + 1e-6;
        const dKopf = `hypot((${u}-${z(kopf.cx)})/${z(kopf.rx)},(${v}-${z(kopf.cy)})/${z(kopf.ry)})`;
        const kopfTeil = `if(gt(${dKopf},${z(grenze)}),0,(st(2,${teil(kern)});if(gte(ld(2),1),1,${vereint(['ld(2)', ...boegen.map(teil)])})))`;
        // Die Bläschen liegen unten: darüber (samt weicher Kante) sind sie 0.
        const oben = Math.min(...blaeschen.map((e) => e.cy - e.ry * (1 + w / (2 * Math.min(e.rx, e.ry))))) - 1e-6;
        const blaeschenTeil = `if(lt(${v},${z(oben)}),0,${vereint(blaeschen.map(teil))})`;
        return `(${vorspann};255*max(${kopfTeil},${blaeschenTeil}))`;
    }
    return `(${vorspann};255*${vereint(ellipsen(form).map(teil))})`;
}
/**
 * Dieselben Formen für den Browser -- in Anteilen (0..1), damit das
 * `<clipPath>` mit `clipPathUnits="objectBoundingBox"` ohne Umrechnung sitzt.
 *
 * Ausnahme: „runde Ecken“ braucht die wahre Grösse, sonst werden die Ecken
 * auf einem breiten Bild oval. Deshalb nimmt auch diese Funktion Breite und
 * Höhe entgegen.
 */
export function svgFormen(form, breite = 1, hoehe = 1, innen = 0, gespiegelt = false) {
    if (form === 'keine')
        return [];
    if (form === 'ecken') {
        const k = Math.min(breite, hoehe) || 1;
        // Nach innen gerückt (7.224): `-abstand/k >= innen` heisst ein um
        // `innen·k` Bildpunkte kleineres Rechteck mit entsprechend kleinerem
        // Eckenradius.
        const rein = innen * k;
        const b = Math.max(breite, 1e-6);
        const h = Math.max(hoehe, 1e-6);
        return [{ art: 'rechteck', x: rein / b, y: rein / h, breite: Math.max(0, 1 - (2 * rein) / b), hoehe: Math.max(0, 1 - (2 * rein) / h), r: Math.max(0, ECKEN_RADIUS * k - rein) / b }];
    }
    return ellipsen(form).map((roh) => {
        const e = rundMachen(form, roh, breite, hoehe);
        // `(1-d)·m >= innen` heisst `d <= 1 - innen/m`: dieselbe Ellipse, um
        // diesen Faktor kleiner.
        const f = innen > 0 ? Math.max(0, 1 - innen / Math.min(e.rx, e.ry)) : 1;
        return { art: 'ellipse', cx: gespiegelt ? 1 - e.cx : e.cx, cy: e.cy, rx: e.rx * f, ry: e.ry * f };
    });
}
/**
 * Dieselbe Lage für das `<clipPath>` im Browser -- als ein `transform` um
 * die Formen herum, nicht als verschobene Formen (7.82b).
 *
 * **Warum eine Matrix und kein `rotate()`:** das `clipPath` rechnet in
 * Anteilen der Ebene (`objectBoundingBox`), und dort ist ein Grad kein Grad
 * -- das Einheitsquadrat wird auf ein 16:9-Rechteck gezogen, und eine
 * Drehung darin kippt den Kreis zur Ellipse. Die Matrix hier ist genau die
 * Drehung, in Bildpunkten gemeint und in Anteile umgerechnet
 * (`S⁻¹·R·S` mit `S = diag(breite, hoehe)`) -- also dasselbe, was
 * `insFeld()` rückwärts tut und der Ausdruck im Export vorwärts.
 *
 * `null` heisst: die Form steht, wo sie immer stand, und es braucht gar
 * keinen `transform`.
 */
export function svgLage(lage, breite = 1, hoehe = 1) {
    if (maskeMittig(lage))
        return null;
    const t = (lage.drehung * Math.PI) / 180;
    const c = Math.cos(t);
    const s = Math.sin(t);
    const b = breite || 1;
    const h = hoehe || 1;
    const m = [z(c), z((s * b) / h), z((-s * h) / b), z(c), '0', '0'].join(',');
    return `translate(${z(lage.x)},${z(lage.y)}) matrix(${m}) scale(${z(lage.groesse)}) translate(-0.5,-0.5)`;
}
//# sourceMappingURL=maske.js.map